import { FunctionComponent, useCallback, useState } from 'react'
import styled, { css } from 'styled-components'

import { type Image } from '@utils/cook/types'
import { CookWidget, JSONTypeForCookWidget } from '@widgets/types'
import { mobileCSS } from '@measures/responsive'

import useIsAuthResolved from '@hooks/useIsAuthResolved'
import useFeatureFlagsSkeleton from '@hooks/useFeatureFlagsSkeleton'
import useUser from '@hooks/useUser'

import FollowMyTeamOverviewComponent from '@components/FollowMyTeamOverview'
import FollowMyTeamOverviewBanner from '@components/FollowMyTeamOverview/FollowMyTeamOverviewBanner'
import FollowMyTeamDrawer from '@components/FollowMyTeamOverview/FollowMyTeamDrawer'

export interface FollowMyTeamOverviewAPIProps {
  title: string
  backgroundImage: Image<'16_9'>
  image: Image<'free'>
}

const FollowMyTeamOverviewWrapper = styled.div`
  height: 80px;

  ${mobileCSS(css`
    height: 64px;
  `)};
`

const FollowMyTeamOverview: FunctionComponent<FollowMyTeamOverviewAPIProps> = (
  props
) => {
  const { title, backgroundImage, image } = props

  const { isFollowMyTeamEnabled } = useFeatureFlagsSkeleton()
  const user = useUser()
  const isAuthResolved = useIsAuthResolved()

  const [isFollowMyTeamDrawerOpen, setIsFollowMyTeamDrawerOpen] =
    useState(false)
  const [showMyTeams, setShowMyTeams] = useState(false)
  const [showSelectTeam, setShowSelectTeam] = useState(false)

  const handleBannerClick = useCallback(() => {
    setShowSelectTeam(true)
    setIsFollowMyTeamDrawerOpen(true)
  }, [setIsFollowMyTeamDrawerOpen])

  const handleSettingsClick = useCallback(() => {
    setShowMyTeams(true)
    setIsFollowMyTeamDrawerOpen(true)
  }, [])

  const handleAddTeamClick = useCallback(() => {
    setShowSelectTeam(true)
    setIsFollowMyTeamDrawerOpen(true)
  }, [setIsFollowMyTeamDrawerOpen])

  const handleCloseDrawer = useCallback(() => {
    setIsFollowMyTeamDrawerOpen(false)
    setShowSelectTeam(false)
    setShowMyTeams(false)
  }, [])

  if (!isFollowMyTeamEnabled) {
    return null
  }

  return (
    <FollowMyTeamOverviewWrapper>
      {isAuthResolved ? (
        !!user ? (
          <FollowMyTeamOverviewComponent
            backgroundImage={backgroundImage}
            image={image}
            title={title}
            onBannerClick={handleBannerClick}
            onSettingsClick={handleSettingsClick}
            onAddTeamClick={handleAddTeamClick}
          />
        ) : (
          <FollowMyTeamOverviewBanner
            state="notLoggedIn"
            backgroundImage={backgroundImage}
            image={image}
            title={title}
            onClick={handleBannerClick}
          />
        )
      ) : null}
      {isFollowMyTeamDrawerOpen && (
        <FollowMyTeamDrawer onClose={handleCloseDrawer}>
          {showSelectTeam && <>TBD: Select Team</>}
          {showMyTeams && <>TBD: List of My Followed Teams</>}
        </FollowMyTeamDrawer>
      )}
    </FollowMyTeamOverviewWrapper>
  )
}

const widget = {
  kind: ['follow-my-team-overview'],
  component: FollowMyTeamOverview,
} as const satisfies CookWidget

export type WidgetType = typeof widget

export type JSONWidgetType = JSONTypeForCookWidget<WidgetType>

export default widget
